import React, { useEffect, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useSetAtom } from 'jotai'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Box as BvnkNetworkContainer,
  Box as CompanyInfoContainer,
  Box as Container,
  Icon,
} from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { NetworkConnectionViewTypes } from 'constants/bvnkNetwork'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useAccountPreferencesQuery } from 'hooks/queries'
import { NetworkConnectionViewType } from 'types/bvnkNetwork'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { BvnkNetworkSheet } from './BvnkNetworkSheet'
import BvnkNetworkTabs from './components/BvnkNetworkTabs'
import CompanyInfo from './components/CompanyInfo'

export interface CompanyDetails {
  id: number
  name: string
  description: string
  logo: string
}

export const BvnkNetwork = () => {
  const { t } = useTranslation()
  const setAction = useSetAtom(actionAtom)

  const { currentAccount } = useAccountsContext()

  const { data: accountPreferences = {}, isPending: isLoadingAccountPreferences } =
    useAccountPreferencesQuery()

  const { tab } = queryString.parse(window.location.search) as { tab?: NetworkConnectionViewType }

  const [activeTab, setActiveTab] = useState(tab || NetworkConnectionViewTypes.CONNECTIONS_VIEW)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>({
    id: currentAccount?.id,
    name: currentAccount?.name,
    description: '',
    logo: '',
  })

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.BVNK_NETWORK, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useEffect(() => {
    const details = {
      id: currentAccount?.id,
      name: currentAccount?.name,
      description: accountPreferences?.companyDescription,
      logo: '',
    }
    setCompanyDetails(details)
  }, [accountPreferences?.companyDescription, currentAccount?.name])

  const onEditClickHandler = () => {
    setAction({
      actionId: ActionIds.NETWORK_EDIT_COMPANY,
      header: t('bvnkNetwork.editCompanyInfo'),
      onClose: () => setIsSheetOpen(false),
      meta: {
        companyDetails,
      },
    })
    setIsSheetOpen(true)
  }

  const onDiscoverMemberClickHandler = () => {
    setAction({
      actionId: ActionIds.NETWORK_DISCOVER_MEMBERS,
      header: t('bvnkNetwork.discoverMembers'),
      onClose: () => setIsSheetOpen(false),
    })
    setIsSheetOpen(true)
  }

  return (
    <>
      <PageHeader
        description={
          <p className="text-body-regular text-foreground-subdued">
            {t('bvnkNetwork.pageDescription')}
            {/* TODO: Add once FAQ has been added */}
            {/* <TextAction onClick={() => null} text={t('bvnkNetwork.readFaq')} /> */}
          </p>
        }
        renderRightSection={
          <Button
            variant="secondary"
            onClick={onDiscoverMemberClickHandler}
            name={t('bvnkNetwork.discoverMembers')}
            iconElement={<Icon name="MagnifyingGlass" size="medium" />}
          >
            {t('bvnkNetwork.discoverMembers')}
          </Button>
        }
      />
      <Container flexCol className="md:flex-row" gapX={24} gapY={24} width="full">
        <BvnkNetworkContainer flexCol width="full" className="md:w-2/3 break-words">
          <BvnkNetworkTabs queryTab={tab} activeTab={activeTab} setActiveTab={setActiveTab} />
        </BvnkNetworkContainer>

        {companyDetails && (
          <CompanyInfoContainer className="md:w-1/3 xxs:pt-4 xxs:flex-row">
            <CompanyInfo
              companyDescription={companyDetails?.description}
              companyName={companyDetails?.name}
              isLoading={isLoadingAccountPreferences}
              onEditClickHandler={onEditClickHandler}
            />
          </CompanyInfoContainer>
        )}

        <BvnkNetworkSheet isSheetOpen={isSheetOpen} />
      </Container>
    </>
  )
}
